/* eslint-disable @typescript-eslint/only-throw-error */
import { observable, action, runInAction, makeObservable } from 'mobx';
import { injectable, inject } from '@servicetitan/react-ioc';
import * as jstz from 'jstz';
import * as queryString from 'query-string';
import { AxiosError, ExceptionResponse } from 'utils';
import { InputFieldState, CheckboxFieldState } from '@servicetitan/form';
import { FormState } from 'formstate';
import { OAuthResponseMode, OAuthResponseType, OktaAuth } from '@okta/okta-auth-js';
import { IdentitySystemApi, LoginData } from '../../api/identity.system.api';
import { LoginApi } from '../api/login.api';
import { AuthApi } from '../../api/auth.api';
import Cookies from 'js-cookie';
import { NotificationsService } from '@servicetitan/notifications';

export enum LoginState {
    Success,
    ResetPasswordRequest,
}

export interface MultipleAccountData {
    UserName: string;
    Type: string;
}

const required = (value: string) => value.length === 0 && 'cannot be empty string';

@injectable()
export class LoginStore {
    @observable isInitializing = false;
    @observable isLoading = false;
    @observable loginState?: LoginState;
    @observable isTurnstileInteractive = false;
    @observable isCaptchaRequired = false;
    @observable isCaptchaVerificationEnabled = false;
    @observable environmentInfo?: string;
    @observable error?: string;
    @observable areInputsErrored = false;
    multipleAccountData: MultipleAccountData[] = [];
    redirectUrl = '';
    form = new FormState({
        username: new InputFieldState('').validators(required),
        password: new InputFieldState('').validators(required),
        shouldRemember: new CheckboxFieldState(false),
        passwordVisbility: new CheckboxFieldState(false),
    });
    showAadRedirectMessage = false;

    private identitySystemApi!: IdentitySystemApi;

    constructor(
        @inject(IdentitySystemApi) identitySystemApi: IdentitySystemApi,
        @inject(NotificationsService) private notificationsService: NotificationsService
    ) {
        makeObservable(this);

        this.identitySystemApi = identitySystemApi;
    }

    @action
    initialize = async () => {
        const parsedUrls = this.parseUrls();
        const stLoc = parsedUrls.stLoc;

        if (
            stLoc &&
            (stLoc.includes('servicetitan.com') ||
                stLoc.includes('st.dev') ||
                stLoc.includes('://localhost'))
        ) {
            const authApiInstance = new AuthApi(stLoc);

            const { data } = await authApiInstance.getEnvironmentInfo(false);
            runInAction(() => {
                this.environmentInfo = data?.environmentName;
            });
        }
    };

    @action
    onUsernameChange = (_0: any, data: { value: string }) => {
        this.areInputsErrored = false;
        this.form.$.username.onChange(data.value);
    };

    @action
    onPasswordChange = (_0: any, data: { value: string }) => {
        this.areInputsErrored = false;
        this.form.$.password.onChange(data.value);
    };

    @action
    updateLoginState = (loginState: LoginState, data?: string | boolean | {}) => {
        const parsedUrls = this.parseUrls();
        if (loginState === LoginState.Success) {
            if (typeof data === 'string') {
                if (data.startsWith('http') || data.startsWith('/')) {
                    this.redirectUrl = data;
                } else {
                    this.redirectUrl = '/' + data;
                }
            } else {
                this.redirectUrl = '/';
            }
        } else {
            this.redirectUrl = '/auth/reset-password/request';
            /*
             * The following lines of code is a temporary solution for the IUM-1031.
             * When we got to the login page from the Community portal there is no redirect_uri parameter
             * and no information on what instance of ServiceTitan app should take care of the password reset flow.
             * So, if there is no redirect_uri and we are on production instance of Okta (https://id.servicetitan.com)
             * then let's redirect to production instance of ServiceTitan for the password reset.
             */
            if (!parsedUrls.stLoc && window.location.origin === 'https://id.servicetitan.com') {
                this.redirectUrl = 'https://go.servicetitan.com' + this.redirectUrl;
            }
        }
        if (this.redirectUrl?.startsWith('/')) {
            this.redirectUrl = parsedUrls.stLoc + this.redirectUrl;
        }

        const currentParams = new URLSearchParams(location.search);
        const context = currentParams.get('contextId');
        const identityTrace = currentParams.get('identityTrace');
        const newParams = new URLSearchParams();
        if (context) {
            newParams.set('contextId', context);
        }
        if (identityTrace) {
            newParams.set('identityTrace', identityTrace);
        }
        if (context || identityTrace) {
            this.redirectUrl = this.redirectUrl + `?${newParams.toString()}`;
        }
        window.location.href = this.redirectUrl;
        this.loginState = loginState;
    };

    parseUrls = () => {
        const queryParams = this.parseQueryString(window.location.href);
        const uri = queryParams.st_uri ?? queryParams.redirect_uri ?? window.location.origin;
        const loc = new URL(uri);
        const tsLocation = new URL(queryParams.redirect_uri);
        return <ParsedUrls>{
            stLoc: loc.origin,
            tsLoc: tsLocation.origin,
            parsedRedirect: queryParams.redirect_uri ?? undefined,
        };
    };

    parseQueryString = (queryString: string) => {
        let qStr = queryString;
        const index = queryString.indexOf('?');
        if (index >= 0) {
            qStr = qStr.substring(index + 1, qStr.length);
        }
        const parts = qStr.split('&');
        const result: { [key: string]: string } = {};
        parts.forEach(function (part) {
            if (part === '') {
                return;
            }
            const keyVal = part.split('=');
            if (keyVal.length > 0) {
                if (keyVal.length > 1) {
                    result[keyVal[0]] = decodeURIComponent(keyVal[1]);
                } else {
                    result[keyVal[0]] = '';
                }
            }
        });
        return result;
    };

    @action
    login = async () => {
        this.isLoading = true;
        try {
            this.form.$.username.onChange(this.form.$.username.value?.trim());
            await this.form.validate();

            if (this.form.hasError) {
                return;
            }
            this.handleCaptchaError(false);

            const queryParams = queryString.parse(window.location.search);
            const { username, password } = this.form.$;
            const loginInfo = await this.prepareLoginInfo(
                username.value,
                (queryParams.identityTrace as string) ?? null
            );
            if (this.processRedirection(loginInfo)) {
                return;
            }

            const authClient = new OktaAuth({
                issuer: window.location.origin + '/oauth2/default',
                clientId: (queryParams.client_id as string) ?? undefined,
                pkce: queryParams.pkce ? queryParams.pkce === 'true' : false,
            });

            // Bootstrap the AuthJS Client
            authClient.options.redirectUri = (queryParams.redirect_uri as string) ?? undefined;
            authClient.options.state = (queryParams.state as string) ?? undefined;
            authClient.options.responseMode =
                (queryParams.response_mode as OAuthResponseMode) ?? undefined;
            authClient.options.responseType =
                (queryParams.response_type as OAuthResponseType) ?? undefined;
            authClient.options.scopes = (queryParams.scope as string)?.split(' ') ?? undefined;

            const loginname = queryParams.loginPrefix
                ? queryParams.loginPrefix + username.value
                : username.value;

            authClient
                .signInWithCredentials({
                    username: loginname,
                    password: password.value,
                })
                .then(transaction => {
                    if (transaction.status === 'SUCCESS') {
                        if (queryParams.client_id) {
                            authClient.token.getWithRedirect({
                                sessionToken: transaction.sessionToken,
                                nonce: (queryParams.nonce as string) ?? undefined,
                                codeChallenge: (queryParams.code_challenge as string) ?? undefined,
                                codeChallengeMethod:
                                    (queryParams.code_challenge_method as string) ?? undefined,
                            });
                        } else {
                            authClient.session.setCookieAndRedirect(
                                transaction.sessionToken,
                                (queryParams.redirect_uri ?? queryParams.fromURI)
                                    ? window.location.origin + queryParams.fromURI
                                    : window.location.origin + '/user/notifications'
                            );
                        }
                    } else if (transaction.status === 'LOCKED_OUT') {
                        // eslint-disable-next-line no-console
                        console.warn('User locked out.');
                        this.showError(
                            "You've exceeded the number of attempts. Please try again in a few minutes"
                        );
                    }
                })
                .catch(async err => {
                    if (queryParams.nofallback) {
                        runInAction(() => {
                            // eslint-disable-next-line no-console
                            console.info('Parameter nofallback was provided');
                            this.showError(
                                "Those credentials don't look right. Please try again.",
                                true
                            );
                            throw this.error;
                        });
                    } else {
                        try {
                            await this.processLoginFallback(err.xhr?.responseText, loginInfo);
                        } catch (e) {
                            runInAction(() => {
                                // eslint-disable-next-line no-console
                                console.error('Fallback login failed:' + e);
                                this.showError(
                                    "Those credentials don't look right. Please try again.",
                                    true
                                );
                                throw this.error;
                            });
                        }
                    }
                });
        } finally {
            this.isLoading = false;
        }
    };

    processRedirection = (loginInfo: LoginInfo) => {
        if (loginInfo.loginData) {
            if (loginInfo.loginData?.redirectUri) {
                let redirectUri = loginInfo.loginData?.redirectUri;
                if (loginInfo.contextId != null) {
                    redirectUri = redirectUri + `&contextId=${loginInfo.contextId}`;
                }
                // redirectUri should be non-null only if user must login via AAD
                window.location.href = loginInfo.tenantName
                    ? redirectUri + `&st_tenant=${loginInfo.tenantName}`
                    : redirectUri;
                return true;
            }
        }
        return false;
    };

    processLoginFallback = async (originalError: string, loginInfo: LoginInfo) => {
        if (loginInfo.loginData) {
            // not sure if we still need all these conditions
            if (loginInfo.loginData?.redirectUri) {
                window.location.href = loginInfo.loginData?.redirectUri;
            } else if (loginInfo.loginData?.loginUri) {
                try {
                    await this.processFallback(loginInfo);
                } catch (e) {
                    runInAction(() => {
                        const error = e as AxiosError<ExceptionResponse>;
                        if (error.response?.data.Error.Code) {
                            this.showError(error.response!.data.Error.Message);
                        } else {
                            this.showError(
                                typeof e === 'string' ? e : 'Connection error. Please try again.'
                            );
                        }
                        throw this.error;
                    });
                }
            }
        } else {
            runInAction(() => {
                // eslint-disable-next-line no-console
                console.error(originalError);
                this.showError("Those credentials don't look right. Please try again.", true);
            });
        }
    };

    processFallback = async (loginInfo: LoginInfo) => {
        const requestData = {
            username: this.form.$.username.$,
            password: this.form.$.password.$,
            contextId: loginInfo.contextId,
            identityTrace: loginInfo.identityTrace,
            timezone: jstz.determine().name(),
            isPersistent: this.form.$.shouldRemember.$,
            returnUrl: loginInfo.parsedRedirect,
            tenantName: loginInfo.tenantName,
        };

        const loginApiInstance = new LoginApi(loginInfo.tokenServerUrl);
        const fallbackLoginResponse = await loginApiInstance.fallbackLogin(requestData);
        const data: string | boolean | any = fallbackLoginResponse.data;

        runInAction(() => {
            if (data.errorMessage) {
                this.showError(data.errorMessage);
                throw this.error;
            } else if (data?.Error && typeof data.Error === 'object') {
                this.showError(data.Error.Message);
                throw this.error;
            }

            const callbackUrl =
                loginInfo.tokenServerUrl +
                '/fallback-callback?identityTrace=' +
                encodeURIComponent(loginInfo.identityTrace ?? '') +
                '&state=' +
                encodeURIComponent(data.state);
            window.setTimeout(() => {
                window.location.href = callbackUrl;
            }, 1);
        });
    };

    prepareLoginInfo = async (userName: string, identityTrace: string | null) => {
        const parsedUrls = this.parseUrls();
        const stLoc = parsedUrls.stLoc;
        const loginInfo = <LoginInfo>{
            identityTrace,
            parsedRedirect: parsedUrls.parsedRedirect,
        };

        if (
            stLoc &&
            (stLoc.includes('servicetitan.com') ||
                stLoc.includes('st.dev') ||
                stLoc.includes('://localhost'))
        ) {
            const queryParams = queryString.parse(window.location.search);
            loginInfo.contextId = queryParams.contextId as string;
            if (queryParams.newfallback) {
                loginInfo.newfallback = queryParams.newfallback as string;
            }
            loginInfo.origin = stLoc;
            loginInfo.tokenServerUrl = parsedUrls.tsLoc;

            const identitySystemApiInstance = new IdentitySystemApi(stLoc);

            let retryCount = 0;
            let tenantName = Cookies.get('st_tenant') ?? null;
            let tenantNameFromHeader: string | null | undefined;
            let success = false;
            while (retryCount < 2) {
                // eslint-disable-next-line no-await-in-loop
                await identitySystemApiInstance
                    .getLoginData(userName, identityTrace, tenantName)
                    .then(
                        res => {
                            tenantNameFromHeader = res.headers['st-tenant'];

                            if (res.data) {
                                loginInfo.loginData = res.data;
                                success = true;
                            }
                        },
                        err => {
                            if (err.response) {
                                tenantNameFromHeader = err.response.headers['st-tenant'];
                            }
                            // eslint-disable-next-line no-console
                            console.error(err.message);
                        }
                    );

                if (tenantNameFromHeader) {
                    tenantName = tenantNameFromHeader;
                    Cookies.set('st_tenant', tenantNameFromHeader, { expires: 30, path: '' });
                }

                if (tenantName && tenantName.length > 0) {
                    loginInfo.tenantName = tenantName;
                }

                if (success) {
                    break;
                }

                retryCount += 1;
            }
        }

        return loginInfo;
    };

    @action
    handleCaptchaError = (value = true) => {
        this.isCaptchaRequired = value;
    };

    @action
    throwError = (e: any) => {
        this.showError(e);
        this.isLoading = false;
    };

    getRedirectUrlToTokenServer = (idp: string) => {
        const urls = this.parseUrls();
        const redirectUrl = new URL(urls.parsedRedirect);
        const ctxId = queryString.parse(window.location.search).contextId;
        return ctxId
            ? redirectUrl.origin + '/auth-with-context/?idp=' + idp + '&contextId=' + ctxId
            : null;
    };

    @action
    markTurnstileInteractive = () => {
        this.isTurnstileInteractive = true;
    };

    @action
    private showError = (error: string, highlightInputs?: boolean) => {
        this.error = error;
        this.areInputsErrored = highlightInputs ?? false;
        this.notificationsService.error({
            title: 'Sign in failed',
            message: error,
            duration: 0,
        });
    };
}

interface LoginInfo {
    identityTrace: string | null;
    parsedRedirect: string;
    origin?: string;
    tokenServerUrl?: string;
    tenantName?: string;
    contextId?: string;
    newfallback?: string;
    loginData?: LoginData;
}

interface ParsedUrls {
    stLoc: string;
    tsLoc: string;
    parsedRedirect: string;
}
